/**
 * GlobeArcTransition — fullscreen Three.js Earth that traces a glowing
 * great-circle arc from `fromLat/fromLon` → `toLat/toLon`, drops the
 * gold marker at the destination, then fades out and fires
 * `onComplete`. Used when the reader clicks a cross-reference link
 * inside ContentPage / DemoChainPage so the spatial relationship
 * between the two chains lands viscerally before the next chain
 * scaffolds in.
 *
 * Apr 29 2026 Lead brief: "Cross reference globe arcs."
 *
 * The arc is drawn as a TubeGeometry along a slerp'd great-circle plus a
 * 0.32 R altitude bow so it reads as flight-path, not chord-through-the-
 * planet. Stroke length grows from 0 → 1 over `durationMs` (default
 * 1900ms) with a cubic-out ease so the head accelerates then settles.
 */
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const EARTH_TEXTURE_LIVE = '/textures/earth-live.jpg';
const EARTH_TEXTURE = '/textures/earth-day.jpg';
const EARTH_TEXTURE_FALLBACK = '/textures/earth-blue-marble.jpg';
const RADIUS = 1.8;

function latLngToVec3(lat, lng, r = RADIUS) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(r * Math.sin(phi) * Math.cos(theta)),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}

function rotationForLatLon(lat, lon) {
  const rotY = -((lon + 180) * Math.PI / 180) - Math.PI / 2;
  const rotX = (lat * Math.PI) / 180;
  return { rotX, rotY };
}

// Build a smooth great-circle Catmull-Rom curve from a → b on the
// sphere, lifted up by `bowAltitude` * R at the apex so it reads as a
// flight path arc rather than a chord through the planet.
function buildArcCurve(a, b, segments = 80, bowAltitude = 0.32) {
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    // Slerp on the unit sphere
    const slerped = a.clone().normalize().lerp(b.clone().normalize(), t).normalize();
    // Lift altitude with a sine bow that peaks at t=0.5
    const altitude = RADIUS * (1 + bowAltitude * Math.sin(Math.PI * t));
    points.push(slerped.multiplyScalar(altitude));
  }
  return new THREE.CatmullRomCurve3(points);
}

export default function GlobeArcTransition({
  fromLat = 9.0,
  fromLon = 38.0,
  toLat = 41.9,
  toLon = 12.5,
  fromLabel = '',
  toLabel = '',
  durationMs = 1900,
  fadeOutMs = 500,
  onComplete,
  testId = 'globe-arc-transition',
}) {
  const mountRef = useRef(null);
  const fadeRef = useRef(null);
  const completedRef = useRef(false);

  useEffect(() => {
    const mountEl = mountRef.current;
    if (!mountEl) return;

    const w = mountEl.clientWidth || window.innerWidth;
    const h = mountEl.clientHeight || window.innerHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 0, 6);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;
    mountEl.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const sun = new THREE.DirectionalLight(0xffffff, 1.4);
    sun.position.set(-5, 3, 4);
    scene.add(sun);

    // Earth — orient so the arc midpoint faces the camera.
    const earthGeo = new THREE.SphereGeometry(RADIUS, 64, 64);
    const earthMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 1,
      metalness: 0,
      emissive: 0x0a0e14,
      emissiveIntensity: 0.12,
    });
    const earth = new THREE.Mesh(earthGeo, earthMat);
    scene.add(earth);

    // Camera-facing orientation: aim the midpoint of the great circle.
    const midLat = (fromLat + toLat) / 2;
    const midLon = (fromLon + toLon) / 2;
    const r = rotationForLatLon(midLat, midLon);
    earth.rotation.set(r.rotX, r.rotY, 0);

    // Arc curve in earth-local coordinates so it stays glued to the
    // surface as we tilt the planet.
    const aLocal = latLngToVec3(fromLat, fromLon, RADIUS);
    const bLocal = latLngToVec3(toLat, toLon, RADIUS);
    const arcCurve = buildArcCurve(aLocal, bLocal, 96, 0.32);

    // Tube geometry — draw progressively by adjusting drawRange on the
    // index buffer. Total radial segments = 96 * 8 verts; we'll grow
    // the index range from 0 → max over `durationMs`.
    const tubeGeo = new THREE.TubeGeometry(arcCurve, 96, 0.018, 8, false);
    const tubeMat = new THREE.MeshBasicMaterial({
      color: 0xffd700,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    });
    const tube = new THREE.Mesh(tubeGeo, tubeMat);
    tube.geometry.setDrawRange(0, 0); // start hidden
    earth.add(tube);

    // Glow shell behind the tube for the cinematic halo.
    const glowGeo = new THREE.TubeGeometry(arcCurve, 96, 0.045, 10, false);
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0xffd700,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
      side: THREE.BackSide,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    glow.geometry.setDrawRange(0, 0);
    earth.add(glow);

    // Markers
    const markerGeo = new THREE.SphereGeometry(0.045, 16, 16);
    const fromMarker = new THREE.Mesh(
      markerGeo,
      new THREE.MeshBasicMaterial({ color: 0x00f0ff }),
    );
    const toMarker = new THREE.Mesh(
      markerGeo,
      new THREE.MeshBasicMaterial({ color: 0xffd700, transparent: true, opacity: 0 }),
    );
    fromMarker.position.copy(latLngToVec3(fromLat, fromLon, RADIUS * 1.012));
    toMarker.position.copy(latLngToVec3(toLat, toLon, RADIUS * 1.012));
    earth.add(fromMarker, toMarker);

    // Atmosphere
    const atmoGeo = new THREE.SphereGeometry(RADIUS * 1.045, 64, 64);
    const atmoMat = new THREE.MeshBasicMaterial({
      color: 0x4080ff, transparent: true, opacity: 0.1, side: THREE.BackSide,
    });
    scene.add(new THREE.Mesh(atmoGeo, atmoMat));

    // Texture — live preferred, day-marble fallbacks.
    const loader = new THREE.TextureLoader();
    let cancelled = false;
    const apply = (tex) => {
      if (cancelled) { tex.dispose(); return; }
      tex.colorSpace = THREE.SRGBColorSpace;
      earthMat.map = tex;
      earthMat.needsUpdate = true;
    };
    loader.load(
      EARTH_TEXTURE,
      apply,
      undefined,
      () => loader.load(EARTH_TEXTURE_FALLBACK, apply, undefined, () => {}),
    );

    const tubeIndexCount = tubeGeo.index ? tubeGeo.index.count : 0;
    const glowIndexCount = glowGeo.index ? glowGeo.index.count : 0;
    const start = performance.now();
    let raf;
    const tick = () => {
      const t = (performance.now() - start) / durationMs;
      const clamped = Math.max(0, Math.min(1, t));
      // Cubic-out — fast at start, settles toward the end.
      const eased = 1 - Math.pow(1 - clamped, 3);
      tube.geometry.setDrawRange(0, Math.floor(tubeIndexCount * eased));
      glow.geometry.setDrawRange(0, Math.floor(glowIndexCount * eased));
      // Destination marker fades in over the last 25% of the animation.
      const arrival = Math.max(0, Math.min(1, (clamped - 0.75) * 4));
      toMarker.material.opacity = arrival;
      toMarker.scale.setScalar(1 + 0.6 * arrival);
      // Slight planet drift toward the destination so the arc head tracks
      // toward camera-center on landing.
      const driftLat = midLat + (toLat - midLat) * eased * 0.18;
      const driftLon = midLon + (toLon - midLon) * eased * 0.18;
      const dr = rotationForLatLon(driftLat, driftLon);
      earth.rotation.set(dr.rotX, dr.rotY, 0);
      renderer.render(scene, camera);
      if (clamped >= 1) {
        if (!completedRef.current) {
          completedRef.current = true;
          if (fadeRef.current) fadeRef.current.style.opacity = '0';
          setTimeout(() => onComplete?.(), fadeOutMs);
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      const ww = mountEl.clientWidth || window.innerWidth;
      const hh = mountEl.clientHeight || window.innerHeight;
      renderer.setSize(ww, hh);
      camera.aspect = ww / hh;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      try { mountEl.removeChild(renderer.domElement); } catch { /* ignore */ }
      renderer.dispose();
      earthGeo.dispose();
      earthMat.dispose();
      tubeGeo.dispose();
      tubeMat.dispose();
      glowGeo.dispose();
      glowMat.dispose();
      markerGeo.dispose();
      atmoGeo.dispose();
      atmoMat.dispose();
    };
  }, [fromLat, fromLon, toLat, toLon, durationMs, fadeOutMs, onComplete]);

  return (
    <div
      ref={fadeRef}
      className="fixed inset-0 z-[105] pointer-events-none"
      data-testid={testId}
      style={{
        background: '#000',
        opacity: 1,
        transition: `opacity ${fadeOutMs}ms ease-out`,
      }}
    >
      <div ref={mountRef} className="absolute inset-0" />
      <div
        className="absolute top-6 left-6 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#D4A84A]/85"
        data-testid={`${testId}-tag`}
      >
        ⟨ Cross-reference ⟩{fromLabel && toLabel ? ` ${fromLabel} → ${toLabel}` : ''}
      </div>
    </div>
  );
}
